import React from 'react';
import Icon from '@avtechsoftware/react-component-library/src/components/common/Icon/Icon';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useSelector } from 'react-redux';
import ChannelKey from '@avtechsoftware/ra-javascript-utilities/src/classes/ChannelKey';
import BooleanChannel from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/BooleanChannel/BooleanChannel';
import RaaExtremeTempFaultChannel
  from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/RaaExtremeTempFaultChannel';
import ExtremeTemperatureFaultChannel
  from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/ExtremeTemperatureFaultChannel/ExtremeTemperatureFaultChannel';
import IntegerAsBooleanChannel
  from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/IntegerAsBooleanChannel/IntegerAsBooleanChannel';
import ChannelService from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/ChannelService';
import { OperatorTypeDetails } from '@avtechsoftware/ra-javascript-utilities/src/types/OperatorTypes';
import { formatTimeDifferenceToDisplayDuration, formatTimestampToFullDate } from '../../../../../utility/DateUtility';
import { selectSessionInformation } from '../../../../../redux/slices/sessionSlice';
import ChannelValueWithUnitsFromChannelKeyObject
  from '../../../../common/ChannelValueWithUnitsFromChannelKeyObject/ChannelValueWithUnitsFromChannelKeyObject';
import {
  selectByUuidOrMacAddress,
  selectRoomAlertDeviceList,
  selectSensorMap,
} from '../../../../../redux/slices/deviceList/deviceListSlice';
import { getChannelDisplayData } from '../../../../../utility/channelUtils';
import getChannelValueWithUnitsFromChannelKeyObjectString
  from '../../../../common/ChannelValueWithUnitsFromChannelKeyObject/getChannelValueWithUnitsFromChannelKeyObjectString';
import { getOperatorTypeFromSymbol } from '../../../../xlalerts/alertforms/AlertFormUtility';
import { getMaxComponentLabel } from '../../../../../utility/alertDisplayUtils';

const specialChannelList = [
  BooleanChannel,
  RaaExtremeTempFaultChannel,
  // TODO could be cleaned up by ensuring the same channel type is returned by both apis
  // using below class because ra-api returns channel type with ExtremeTemperatureFaultChannel,
  // but the single alert api returns RaaExtremeTempFaultChannel
  ExtremeTemperatureFaultChannel,
  IntegerAsBooleanChannel,
];
/* istanbul ignore next */
function AlarmHistoryTableRowContent({ alert }) {
  const sessionInformation = useSelector(selectSessionInformation);
  const { timezone } = sessionInformation;
  const sensorMap = useSelector(selectSensorMap);
  const deviceList = useSelector(selectRoomAlertDeviceList);
  const duration = formatTimeDifferenceToDisplayDuration(
    alert.triggerTimestamp,
    alert.clearTimestamp || moment().unix(),
  );
  const {
    deviceName, sensorName, port, channelName,
  } = getChannelDisplayData(
    alert.channelKey, deviceList, sensorMap,
  );
  const channelKey = new ChannelKey(alert.channelKey);
  const channel = ChannelService.getChannelGroupChannel(
    channelKey.channelGroupType,
    channelKey.channelIndex,
    channelKey.channelType,
  );

  const device = useSelector(
    (state) => selectByUuidOrMacAddress(state, channelKey.macAddress),
  );
  const isRAMAXDevice = device && device.type === 'RAMAX';
  const deviceSensorList = sensorMap[channelKey.macAddress] || [];

  let maxComponentLabel = '';
  if (isRAMAXDevice && deviceSensorList && deviceSensorList.length > 0) {
    maxComponentLabel = getMaxComponentLabel(deviceSensorList, channelKey);
  }
  const startOperatorTypeId = getOperatorTypeFromSymbol(alert.startOperator) || alert.startOperator;
  const {
    hysteresisMultiplier,
  } = OperatorTypeDetails[startOperatorTypeId] || 1;

  const handleBoolAndFaultConditionDisplay = (value, isTrigger) => {
    if (channel instanceof BooleanChannel
            && !(channel instanceof ExtremeTemperatureFaultChannel)
            && !(channel instanceof RaaExtremeTempFaultChannel)) {
      return channel.getDisplayValue(value);
    }
    if (channel instanceof ExtremeTemperatureFaultChannel
            || channel instanceof RaaExtremeTempFaultChannel
            || channel instanceof IntegerAsBooleanChannel) {
      const isFaultTriggerCondition = (alert.startThreshold === 1
                && startOperatorTypeId === 3);
      if (isTrigger) {
        return isFaultTriggerCondition ? 'No Fault' : 'Fault';
      }
      return isFaultTriggerCondition ? 'Fault' : 'No Fault';
    }
    return '';
  };

  const triggerDisplayValue = specialChannelList.some((chan) => channel instanceof chan)
    ? handleBoolAndFaultConditionDisplay(alert.startThreshold, false)
    : `${alert.startOperator} ${getChannelValueWithUnitsFromChannelKeyObjectString(sessionInformation, channelKey, alert.startThreshold, alert.customUnit)}`;

  const clearDisplayValue = specialChannelList.some((chan) => channel instanceof chan)
    ? handleBoolAndFaultConditionDisplay(alert.clearThreshold, false)
    : `${alert.clearOperator} ${getChannelValueWithUnitsFromChannelKeyObjectString(sessionInformation, channelKey, (alert.clearThreshold
            + (channel.hysteresisBuffer * hysteresisMultiplier)), alert.customUnit)}`;

  const handleShowClearDisplayValue = () => {
    if (alert.clearValue === 'Alert Edited') {
      return alert.clearValue;
    }
    if (alert.clearValue == null) {
      return '--';
    }
    if (!(channel instanceof BooleanChannel)) {
      return clearDisplayValue;
    }
    return null;
  };

  return (
    <>
      <td>
        <span>{formatTimestampToFullDate(alert.triggerTimestamp, timezone)}</span>
        {' '}
        -
        <br />
        <span>{alert.clearTimestamp != null ? formatTimestampToFullDate(alert.clearTimestamp, timezone) : 'In Alarm'}</span>
      </td>

      <td>
        <span>{duration}</span>
      </td>

      <td>
        <span>{deviceName}</span>
        <br />
        <span>{`${sensorName} ${isRAMAXDevice ? (`- ${maxComponentLabel}`) : ''} (${port}) > ${channelName}`}</span>
      </td>

      <td>
        <b className="text-danger">
          <span>
            <ChannelValueWithUnitsFromChannelKeyObject
              channelKey={channelKey}
              value={alert.triggerValue}
              customUnits={alert.customUnit}
            />
          </span>
        </b>
        {
              !(channel instanceof BooleanChannel) && (
                <>
                    {' '}
                  <span>
                    {triggerDisplayValue}
                  </span>
                </>
              )
        }
      </td>

      <td>
        {(alert.clearValue != null && alert.clearValue !== 'Alert Edited')
            && (
            <span>
              <b className="text-success">
                <span>
                  <ChannelValueWithUnitsFromChannelKeyObject
                    channelKey={channelKey}
                    value={alert.clearValue}
                    customUnits={alert.customUnit}
                  />
                </span>
              </b>
                {' '}
            </span>
            )}

        <span>
          {handleShowClearDisplayValue()}
        </span>
      </td>

      <td>
        {alert.emailActionCount > 0
            && (
            <span>
              <span>{alert.emailActionCount}</span>
                {' '}
              <Icon icon="envelope" />
                {' '}
            </span>
            )}
        {alert.textActionCount > 0
            && (
            <span>
              <span>{alert.textActionCount}</span>
                {' '}
              <Icon icon="laptop-mobile" />
                {' '}
            </span>
            )}
        {alert.postActionCount > 0
            && (
            <span>
              <span>{alert.postActionCount}</span>
              {' '}
              <Icon icon="square-arrow-up-right" />
              {' '}
            </span>
            )}
        {alert.realSmsActionCount > 0
              && (
              <span>
                <span>{alert.realSmsActionCount}</span>
                {' '}
                <Icon icon="message-dots" />
                {' '}
              </span>
              )}
        {alert.pushNotificationActionCount > 0
          && (
          <span>
            <span>{alert.pushNotificationActionCount}</span>
            {' '}
            <Icon icon="mobile-notch" />
            {' '}
          </span>
          )}
      </td>
    </>
  );
}

AlarmHistoryTableRowContent.propTypes = {
  alert: PropTypes.shape(
    {
      triggerTimestamp: PropTypes.string,
      clearTimestamp: PropTypes.string,
      // duration: PropTypes.string,
      // deviceName: PropTypes.string,
      channelGroupSourceName: PropTypes.string,
      triggerValue: PropTypes.string,
      startThreshold: PropTypes.string,
      startOperator: PropTypes.string,
      clearOperator: PropTypes.string,
      clearThreshold: PropTypes.string,
      channelKey: PropTypes.string,
      // triggerDisplayValue: PropTypes.any,
      clearValue: PropTypes.string,
      // clearedDisplayValue: PropTypes.any,
      emailActionCount: PropTypes.number,
      textActionCount: PropTypes.number,
      postActionCount: PropTypes.number,
      realSmsActionCount: PropTypes.number,
      pushNotificationActionCount: PropTypes.number,
      // hasCleared: PropTypes.bool,
      alertId: PropTypes.string,
      customUnit: PropTypes.string,
    },
  ).isRequired,
};

export default AlarmHistoryTableRowContent;
